import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { localizedPath, useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertCircle, FileDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/lib/useSEO";

/**
 * Payment success — polls /api/payments/status/{session_id} until paid.
 * On paid → persists access_token in localStorage for this device and redirects
 * back to /results/{code}?token=... so the user can immediately download the PDF.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { lang } = useLang();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const paypalOrder = params.get("paypal_order");
  const isMidtrans = !!orderId && !sessionId;
  const [state, setState] = useState({ status: "polling", tries: 0 });

  useSEO({
    title: lang === "id" ? "Status Pembayaran — Mindovia" : "Payment Status — Mindovia",
    description: lang === "id" ? "Verifikasi pembayaran pribadi Mindovia." : "Private Mindovia payment verification.",
    url: null,
    robots: "noindex, nofollow, noarchive",
  });

  useEffect(() => {
    const idParam = sessionId || orderId || paypalOrder;
    if (!idParam) { setState({ status: "error", message: "Missing session_id or order_id" }); return; }
    let cancelled = false;
    let tries = 0;
    const statusPath = paypalOrder
      ? `/payments/paypal/status/${encodeURIComponent(paypalOrder)}`
      : isMidtrans
        ? `/payments/midtrans/status/${encodeURIComponent(orderId)}`
        : `/payments/status/${encodeURIComponent(sessionId)}`;
    const poll = async () => {
      if (cancelled) return;
      tries += 1;
      try {
        const { data } = await api.get(statusPath);
        if (data.payment_status === "paid" && data.access_token) {
          const key = `mnd_pdf_token_${data.anonymous_code}`;
          localStorage.setItem(key, JSON.stringify({ token: data.access_token, expires_at: data.expires_at }));
          setState({ status: "paid", code: data.anonymous_code, token: data.access_token, expires_at: data.expires_at });
          return;
        }
        if (["failed", "expired", "refunded", "reversed", "deny", "cancel"].includes(data.payment_status)) {
          setState({ status: "failed", message: `Payment ${data.payment_status}` });
          return;
        }
      } catch (e) {
        if (tries > 30) { setState({ status: "error", message: e?.response?.data?.detail || e.message }); return; }
      }
      setState((p) => ({ ...p, tries }));
      if (tries < 40) setTimeout(poll, 2000);
      else setState({ status: "timeout" });
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, orderId, paypalOrder, isMidtrans]);

  return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-wrap="responsive" data-testid="payment-success-page">
      {state.status === "polling" && (
        <div>
          <div className="w-14 h-14 mx-auto rounded-full bg-cream-100 border border-border animate-pulse" />
          <h1 className="font-display text-2xl mt-6">{lang === "id" ? "Memverifikasi pembayaran…" : "Verifying payment…"}</h1>
          <p className="text-muted-foreground text-sm mt-2">{lang === "id" ? "Tunggu sebentar, ya. Kami sedang mengecek ke penyedia pembayaran." : "Hang on while we check with the payment provider."}</p>
          <div className="text-xs text-muted-foreground mt-2">attempt {state.tries}/40</div>
        </div>
      )}
      {state.status === "paid" && (
        <div>
          <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-600" />
          <h1 className="font-display text-3xl mt-6" data-testid="payment-success-title">{lang === "id" ? "Pembayaran berhasil" : "Payment successful"}</h1>
          <p className="text-muted-foreground mt-2 max-w-md mx-auto">{lang === "id" ? "Laporan lengkapmu sudah siap. Unduh dan simpan, ya, karena tautan ini ada masa berlakunya." : "Your full report is ready. Download and save it, as this link will expire."}</p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <Button onClick={() => nav(`/results/${state.code}?token=${state.token}`)} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-cream-50" data-testid="payment-success-open-btn">
              <FileDown className="w-4 h-4 mr-2" /> {lang === "id" ? "Buka Laporan" : "Open Report"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a href={`/api/public/results/${state.code}/pdf-paid?token=${state.token}&lang=${lang}`} download className="text-sm text-terracotta-700 underline underline-offset-4" data-testid="payment-success-download-btn">{lang === "id" ? "Unduh PDF sekarang" : "Download PDF now"}</a>
          </div>
          <div className="mt-6 p-3 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 max-w-md mx-auto" data-testid="payment-download-warning">
            {lang === "id" ? "Unduh dan simpan laporanmu. Tautan ini hanya berlaku untuk waktu terbatas." : "Download and save your report. This link only works for a limited time."}
          </div>
        </div>
      )}
      {(state.status === "failed" || state.status === "timeout" || state.status === "error") && (
        <div>
          <AlertCircle className="w-14 h-14 mx-auto text-destructive" />
          <h1 className="font-display text-2xl mt-6" data-testid="payment-failed-title">{lang === "id" ? "Verifikasi gagal" : "Verification failed"}</h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">{state.message || (lang === "id" ? "Pembayaran belum berhasil atau melebihi batas waktu. Silakan coba lagi." : "Payment did not complete in time. Please try again.")}</p>
          <div className="mt-6">
            <Link to={localizedPath("/instruments", lang)}><Button variant="outline" className="rounded-full">{lang === "id" ? "Kembali ke daftar tes" : "Back to all tests"}</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
